import { useState, useEffect, useRef, Fragment } from 'react';
import { Outlet, NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Dialog, DialogBackdrop, DialogPanel,
  Popover, PopoverButton, PopoverPanel,
  Disclosure, DisclosureButton, DisclosurePanel,
  Switch, Transition, TransitionChild,
} from '@headlessui/react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { SocketProvider } from '../contexts/SocketContext';
import { NotificationProvider } from '../contexts/NotificationContext';
import { MessagingProvider, useMessaging } from '../contexts/MessagingContext';
import NotificationBell from './NotificationBell';
import NotificationToast from './NotificationToast';
import GroupSwitcherMenuItem from './GroupSwitcherMenuItem';
import ActAsBanner from './ActAsBanner';
import api from '../services/apiService';
import {
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  HomeIcon,
  BuildingOffice2Icon,
  UsersIcon,
  CalendarDaysIcon,
  BanknotesIcon,
  ChatBubbleLeftRightIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ChevronDownIcon,
  UserCircleIcon,
  InformationCircleIcon,
  SunIcon,
  MoonIcon,
  Cog6ToothIcon,
  BookOpenIcon,
  EnvelopeIcon,
  BellIcon,
  DevicePhoneMobileIcon,
  StarIcon,
  ArrowUturnLeftIcon,
  UserGroupIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { Megaphone } from 'lucide-react';

const NAV_BY_ROLE = {
  super_admin: [
    { to: '/admin', label: 'Tableau de bord', icon: HomeIcon, exact: true },
    { to: '/admin/groups', label: 'Groupes', icon: BuildingOffice2Icon },
    { to: '/admin/membres', label: 'Membres', icon: UsersIcon },
    { to: '/admin/tarifs', label: 'Tarifs', icon: BanknotesIcon },
    { to: '/admin/email-templates', label: 'Modèles d\'email', icon: EnvelopeIcon },
    { to: '/admin/notification-settings', label: 'Notifications', icon: BellIcon },
    { to: '/admin/sms', label: 'SMS', icon: DevicePhoneMobileIcon },
    { to: '/admin/temoignages', label: 'Témoignages', icon: StarIcon },
  ],
  group_admin: [
    { to: '/group/settings', label: 'Mon groupe', icon: Cog6ToothIcon },
    { to: '/group/members', label: 'Membres', icon: UsersIcon },
    { to: '/group/meetings', label: 'Réunions', icon: CalendarDaysIcon },
    { to: '/group/events', label: 'Événements', icon: CalendarDaysIcon },
    { to: '/group/cotisations', label: 'Cotisations', icon: BanknotesIcon },
    { to: '/group/email-templates', label: "Modèles d'email", icon: EnvelopeIcon },
    { to: '/group/notification-settings', label: 'Notifications', icon: BellIcon },
    { separator: true, label: 'Mon espace' },
    { to: '/dashboard', label: 'Tableau de bord', icon: HomeIcon },
    { to: '/annuaire', label: 'Annuaire', icon: BookOpenIcon },
    { to: '/recommendations', label: 'Recommandations', icon: Megaphone, isLucide: true },
    { to: '/tete-a-tete', label: 'Tête-à-tête', icon: ChatBubbleLeftRightIcon },
    { to: '/events', label: 'Événements', icon: CalendarDaysIcon },
    { to: '/messages', label: 'Messages', icon: ChatBubbleOvalLeftEllipsisIcon },
    { to: '/temoignages', label: 'Témoignages', icon: StarIcon },
  ],
  member: [
    { to: '/', label: 'Tableau de bord', icon: HomeIcon },
    { to: '/annuaire', label: 'Annuaire', icon: BookOpenIcon },
    { to: '/recommendations', label: 'Recommandations', icon: Megaphone, isLucide: true },
    { to: '/tete-a-tete', label: 'Tête-à-tête', icon: ChatBubbleLeftRightIcon },
    { to: '/events', label: 'Événements', icon: CalendarDaysIcon },
    { to: '/messages', label: 'Messages', icon: ChatBubbleOvalLeftEllipsisIcon },
    { to: '/temoignages', label: 'Témoignages', icon: StarIcon },
  ],
  // Supervisor sees the same pages as group_admin (read-only enforced per-page)
  supervisor: [
    { to: '/group/settings', label: 'Mon groupe', icon: Cog6ToothIcon },
    { to: '/group/members', label: 'Membres', icon: UsersIcon },
    { to: '/group/meetings', label: 'Réunions', icon: CalendarDaysIcon },
    { to: '/group/events', label: 'Événements', icon: CalendarDaysIcon },
    { to: '/group/cotisations', label: 'Cotisations', icon: BanknotesIcon },
    { to: '/group/email-templates', label: "Modèles d'email", icon: EnvelopeIcon },
    { to: '/group/notification-settings', label: 'Notifications', icon: BellIcon },
  ],
};

const ROLE_BADGE = {
  super_admin: { label: 'Super Admin', cls: 'bg-surface-tertiary text-skin-secondary' },
  group_admin: { label: 'Admin Groupe', cls: 'bg-primary-100 text-primary-700' },
  member: { label: 'Membre', cls: 'bg-accent-100 text-accent-700' },
  supervisor: { label: 'Superviseur', cls: 'bg-purple-100 text-purple-700' },
};

function getInitials(user) {
  if (user?.first_name && user?.last_name) {
    return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
  }
  if (user?.email) {
    return user.email[0].toUpperCase();
  }
  return '?';
}

function getDisplayName(user) {
  if (user?.first_name && user?.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  return user?.email ?? '';
}

function UserAvatar({ user, size = 'size-8' }) {
  if (user?.avatar_url) {
    return (
      <img
        src={user.avatar_url}
        alt={getDisplayName(user) || 'Avatar'}
        className={`${size} rounded-full object-cover`}
      />
    );
  }
  return (
    <span className={`inline-flex ${size} items-center justify-center rounded-full bg-primary-100`}>
      <span className="text-xs font-semibold text-primary-700">{getInitials(user)}</span>
    </span>
  );
}

function GroupAvatar({ group, size = 'size-7' }) {
  if (group?.logo) {
    return (
      <img
        src={group.logo}
        alt={group.name || 'Logo'}
        className={`${size} rounded-full object-cover`}
      />
    );
  }
  const initials = group?.name ? group.name[0].toUpperCase() : '?';
  return (
    <span className={`inline-flex ${size} items-center justify-center rounded-full bg-amber-100`}>
      <span className="text-xs font-semibold text-amber-700">{initials}</span>
    </span>
  );
}

function BrandLogo({ user }) {
  if (user?.group_logo) {
    return (
      <img
        src={user.group_logo}
        alt={user.group_name || 'Logo'}
        className="max-h-10 object-contain"
      />
    );
  }
  return (
    <h1>
      <img src="/brand/m-bao-mark.png" alt="m-bao.fr" width={520} height={200} className="block h-10 w-auto dark:hidden" />
      <img src="/brand/m-bao-mark-on-dark.png" alt="m-bao.fr" width={520} height={200} className="hidden h-10 w-auto dark:block" />
    </h1>
  );
}

/* --------------------------------------------------------------------------
   GroupSwitcherList — shared between desktop popover and mobile modal
   -------------------------------------------------------------------------- */
function GroupSwitcherList({ groups, loading, error, onRetry, currentGroupId, onSelect, onExit }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <svg className="h-5 w-5 animate-spin text-amber-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-2 px-3 py-4 text-center">
        <p className="text-sm text-red-600">{error}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="text-xs font-medium text-amber-700 hover:underline"
          >
            Réessayer
          </button>
        )}
      </div>
    );
  }

  if (!groups.length) {
    return (
      <p className="px-3 py-4 text-center text-sm text-skin-tertiary">Aucun groupe actif.</p>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      {groups.map((group) => {
        const isActive = currentGroupId != null && String(group.id) === String(currentGroupId);
        return (
          <button
            key={group.id}
            onClick={() => onSelect(group)}
            className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors ${
              isActive
                ? 'bg-amber-50 ring-1 ring-amber-200'
                : 'hover:bg-hover'
            }`}
          >
            <GroupAvatar group={group} />
            <div className="min-w-0 flex-1">
              <p className={`truncate text-sm font-medium ${isActive ? 'text-amber-700' : 'text-skin-primary'}`}>
                {group.name}
              </p>
              {group.admin_email && (
                <p className="truncate text-xs text-skin-tertiary">{group.admin_email}</p>
              )}
            </div>
            {isActive && (
              <CheckIcon className="h-4 w-4 shrink-0 text-amber-600" />
            )}
          </button>
        );
      })}

      {currentGroupId != null && (
        <div className="mt-1 border-t border-skin pt-1">
          <button
            onClick={onExit}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-50"
          >
            <ArrowUturnLeftIcon className="h-4 w-4 shrink-0" />
            Retour super admin
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
   useGroupsList — lazy fetch + cache for the groups list
   -------------------------------------------------------------------------- */
function useGroupsList(endpoint = '/admin/groups') {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchGroups = async () => {
    if (fetched) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(endpoint);
      if (!mountedRef.current) return;
      setGroups(data.groups ?? []);
      setFetched(true);
    } catch {
      if (!mountedRef.current) return;
      setGroups([]);
      setError('Erreur de chargement des groupes.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const retryFetch = () => {
    setFetched(false);
    setError(null);
    fetchGroups();
  };

  return { groups, loading, error, fetchGroups, retryFetch, fetched };
}

/* --------------------------------------------------------------------------
   SidebarContent
   -------------------------------------------------------------------------- */
function SidebarContent({ navItems, user, badge, onLogout, onNavigate }) {
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();

  return (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-surface-secondary px-6 pb-4">
      {/* Brand */}
      <div className="flex h-16 shrink-0 items-center">
        <BrandLogo user={user} />
      </div>

      {/* Navigation */}
      <nav className="flex flex-1 flex-col">
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          <li>
            <ul role="list" className="-mx-2 space-y-1">
              {navItems.map((item, idx) => {
                if (item.separator) {
                  return (
                    <li key={`sep-${idx}`} className="pt-4 pb-1">
                      <div className="text-xs/6 font-semibold text-skin-tertiary">
                        {item.label}
                      </div>
                    </li>
                  );
                }

                const { to, label, icon: Icon, isLucide, exact } = item;
                const isActive =
                  to === '/' || exact
                    ? location.pathname === to
                    : location.pathname === to || location.pathname.startsWith(to + '/');

                return (
                  <li key={to}>
                    <NavLink
                      to={to}
                      end={to === '/'}
                      onClick={onNavigate}
                      className={`group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold ${
                        isActive
                          ? 'bg-active text-primary-600'
                          : 'text-skin-secondary hover:bg-hover hover:text-primary-600'
                      }`}
                    >
                      {isLucide ? (
                        <Icon
                          className={`h-6 w-6 shrink-0 ${
                            isActive ? 'text-primary-600' : 'text-skin-tertiary group-hover:text-primary-600'
                          }`}
                          strokeWidth={1.5}
                        />
                      ) : (
                        <Icon
                          className={`h-6 w-6 shrink-0 ${
                            isActive ? 'text-primary-600' : 'text-skin-tertiary group-hover:text-primary-600'
                          }`}
                        />
                      )}
                      {label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </li>
          {/* User footer */}
          <li className="-mx-6 mt-auto">
            <Link
              to="/profile"
              onClick={onNavigate}
              className="flex items-center gap-x-4 px-6 py-3 text-sm/6 font-semibold text-skin-primary hover:bg-hover"
            >
              <UserAvatar user={user} />
              <span className="sr-only">Votre profil</span>
              <span aria-hidden="true">{getDisplayName(user)}</span>
            </Link>
            <button
              onClick={toggleTheme}
              className="flex w-full items-center gap-x-4 px-6 py-3 text-sm/6 font-semibold text-skin-primary hover:bg-hover"
            >
              {isDark ? (
                <MoonIcon className="h-6 w-6 shrink-0 text-skin-tertiary" />
              ) : (
                <SunIcon className="h-6 w-6 shrink-0 text-skin-tertiary" />
              )}
              <span>{isDark ? 'Mode clair' : 'Mode sombre'}</span>
            </button>
            <Link
              to="/about"
              onClick={onNavigate}
              className="flex w-full items-center gap-x-4 px-6 py-3 text-sm/6 font-semibold text-skin-secondary hover:bg-hover"
            >
              <InformationCircleIcon className="h-6 w-6 shrink-0 text-skin-tertiary" />
              <span>À propos</span>
            </Link>
            <button
              onClick={onLogout}
              className="flex w-full items-center gap-x-4 px-6 py-3 text-sm/6 font-semibold text-skin-secondary hover:bg-hover"
            >
              <ArrowRightOnRectangleIcon className="h-6 w-6 shrink-0 text-skin-tertiary" />
              <span>Se déconnecter</span>
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
}

/* --------------------------------------------------------------------------
   MobileGroupSwitcherModal
   -------------------------------------------------------------------------- */
function MobileGroupSwitcherModal({ open, onClose, groups, loading, error, onRetry, currentGroupId, onSelect, onExit }) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <TransitionChild
          as={Fragment}
          enter="transition-opacity duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <DialogBackdrop className="fixed inset-0 bg-gray-900/60" />
        </TransitionChild>

        <div className="fixed inset-0 flex items-end justify-center sm:items-center">
          <TransitionChild
            as={Fragment}
            enter="transition-all duration-250 ease-out"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="transition-all duration-150 ease-in"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel className="w-full max-w-sm rounded-t-2xl bg-surface-elevated shadow-xl sm:rounded-xl">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-skin px-4 py-3">
                <div className="flex items-center gap-2">
                  <UserGroupIcon className="h-5 w-5 text-amber-600" />
                  <span className="text-sm font-semibold text-skin-primary">Administrer un groupe</span>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-md p-1 text-skin-tertiary hover:bg-hover hover:text-skin-primary"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* List */}
              <div className="max-h-[60vh] overflow-y-auto px-2 py-2">
                <GroupSwitcherList
                  groups={groups}
                  loading={loading}
                  error={error}
                  onRetry={onRetry}
                  currentGroupId={currentGroupId}
                  onSelect={onSelect}
                  onExit={onExit}
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}

/* --------------------------------------------------------------------------
   AppLayout
   -------------------------------------------------------------------------- */
function AppLayoutInner() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileSwitcherOpen, setMobileSwitcherOpen] = useState(false);

  const { user, logout, signOut, impersonating, impersonateGroup, exitImpersonation, switchGroup } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { unreadCount: messagingUnreadCount } = useMessaging();
  const location = useLocation();
  const navigate = useNavigate();

  // Use the original role when impersonating so the dropdown hits the correct endpoint
  // (super_admin → /admin/groups). Without this, an impersonating super_admin would
  // appear as group_admin and get a 403 from the groups dropdown.
  const effectiveRole = impersonating ? (user?.original_role ?? 'super_admin') : user?.role;
  const groupsEndpoint = effectiveRole === 'supervisor' ? '/supervisor/my-groups' : '/admin/groups';
  const { groups, loading: groupsLoading, error: groupsError, fetchGroups, retryFetch } = useGroupsList(groupsEndpoint);

  const originalRole = effectiveRole;
  const isSuperAdminOrImpersonating = originalRole === 'super_admin' || impersonating;

  // « Agir en tant que » — private messaging and group switching stay closed
  const actingAs = !!user?.act_as;
  const showGroupSwitcher =
    !actingAs && !!user && !['super_admin', 'supervisor'].includes(user.role) && user.has_multiple_groups === true;

  const role = user?.role ?? 'member';
  const roleNavItems = NAV_BY_ROLE[role] ?? NAV_BY_ROLE.member;
  const navItems = actingAs ? roleNavItems.filter((item) => item.to !== '/messages') : roleNavItems;
  const badge = ROLE_BADGE[role];

  // The group_id being impersonated — from user context when impersonating
  const currentImpersonatedGroupId = impersonating ? user?.impersonating_group_id : null;

  const handleExitImpersonation = async () => {
    try {
      await exitImpersonation();
    } catch (err) {
      // Only force-logout on authentication failures (401/403).
      // Do not logout on JS errors like navigation or state update errors.
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        logout();
        navigate('/login');
        return;
      }
      // For any other error, still attempt to navigate back to the admin view.
    }
    navigate('/admin/groups');
  };

  const handleSelectGroup = async (group, closeCallback) => {
    if (String(group.id) === String(currentImpersonatedGroupId)) {
      if (closeCallback) closeCallback();
      return;
    }
    try {
      if (originalRole === 'supervisor') {
        await switchGroup(group.id, '/supervisor');
      } else {
        await impersonateGroup(group.id);
      }
      if (closeCallback) closeCallback();
      navigate('/group/members');
    } catch {
      // silently fail — toast from apiService handles error
    }
  };

  const handleMobileSelect = async (group) => {
    setMobileSwitcherOpen(false);
    await handleSelectGroup(group, null);
  };

  const handleMobileExit = async () => {
    setMobileSwitcherOpen(false);
    await handleExitImpersonation();
  };

  const handleMobileSwitcherOpen = () => {
    fetchGroups();
    setMobileSwitcherOpen(true);
  };

  return (
        <div className="min-h-screen bg-surface">
          {/* Notification toast overlay */}
          <NotificationToast />

          {/* Mobile sidebar drawer */}
          <Transition show={sidebarOpen} as={Fragment}>
            <Dialog onClose={() => setSidebarOpen(false)} className="relative z-50 lg:hidden">
              <TransitionChild
                as={Fragment}
                enter="transition-opacity duration-200"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-150"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <DialogBackdrop className="fixed inset-0 bg-gray-900/80" />
              </TransitionChild>

              <div className="fixed inset-0 flex">
                <TransitionChild
                  as={Fragment}
                  enter="transition-transform duration-250 ease-out"
                  enterFrom="-translate-x-full"
                  enterTo="translate-x-0"
                  leave="transition-transform duration-200 ease-in"
                  leaveFrom="translate-x-0"
                  leaveTo="-translate-x-full"
                >
                  <DialogPanel className="relative mr-16 flex w-full max-w-xs flex-1">
                    <TransitionChild
                      as={Fragment}
                      enter="transition-opacity duration-200"
                      enterFrom="opacity-0"
                      enterTo="opacity-100"
                      leave="transition-opacity duration-150"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <div className="absolute top-0 left-full flex w-16 justify-center pt-5">
                        <button
                          type="button"
                          onClick={() => setSidebarOpen(false)}
                          className="-m-2.5 p-2.5"
                        >
                          <span className="sr-only">Fermer le menu</span>
                          <XMarkIcon className="h-6 w-6 text-white" />
                        </button>
                      </div>
                    </TransitionChild>
                    <SidebarContent
                      navItems={navItems}
                      user={user}
                      badge={badge}
                      onLogout={signOut}
                      onNavigate={() => setSidebarOpen(false)}
                    />
                  </DialogPanel>
                </TransitionChild>
              </div>
            </Dialog>
          </Transition>

          {/* Desktop sidebar */}
          <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
            <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-skin bg-surface-secondary px-6 pb-4">
              {/* Brand / impersonation header */}
              <div className="flex h-16 shrink-0 items-center">
                {impersonating && user?.group_name ? (
                  <div className="flex items-center gap-2.5">
                    <div className="relative">
                      <GroupAvatar group={{ logo: user.group_logo, name: user.group_name }} size="size-9" />
                      <span className="absolute -top-0.5 -right-0.5 block h-3 w-3 rounded-full bg-amber-500 ring-2 ring-surface-secondary" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-skin-primary">{user.group_name}</p>
                      <p className="text-xs text-amber-600">Vue admin groupe</p>
                    </div>
                  </div>
                ) : (
                  <BrandLogo user={user} />
                )}
              </div>

              {/* Navigation */}
              <nav className="flex flex-1 flex-col">
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="-mx-2 space-y-1">
                      {navItems.map((item, idx) => {
                        if (item.separator) {
                          return (
                            <li key={`sep-${idx}`} className="pt-4 pb-1">
                              <div className="text-xs/6 font-semibold text-skin-tertiary">
                                {item.label}
                              </div>
                            </li>
                          );
                        }

                        const { to, label, icon: Icon, isLucide, exact } = item;
                        const isActive =
                          to === '/' || exact
                            ? location.pathname === to
                            : location.pathname === to || location.pathname.startsWith(to + '/');

                        return (
                          <li key={to}>
                            <NavLink
                              to={to}
                              end={to === '/'}
                              className={`group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold ${
                                isActive
                                  ? 'bg-active text-primary-600'
                                  : 'text-skin-secondary hover:bg-hover hover:text-primary-600'
                              }`}
                            >
                              {isLucide ? (
                                <Icon
                                  className={`h-6 w-6 shrink-0 ${
                                    isActive ? 'text-primary-600' : 'text-skin-tertiary group-hover:text-primary-600'
                                  }`}
                                  strokeWidth={1.5}
                                />
                              ) : (
                                <Icon
                                  className={`h-6 w-6 shrink-0 ${
                                    isActive ? 'text-primary-600' : 'text-skin-tertiary group-hover:text-primary-600'
                                  }`}
                                />
                              )}
                              {label}
                            </NavLink>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                  {/* User footer */}
                  <li className="-mx-6 mt-auto">
                    <Link
                      to="/profile"
                      className="flex items-center gap-x-4 px-6 py-3 text-sm/6 font-semibold text-skin-primary hover:bg-hover"
                    >
                      <UserAvatar user={user} />
                      <span className="sr-only">Votre profil</span>
                      <span aria-hidden="true">{getDisplayName(user)}</span>
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>
          </div>

          {/* Main content area */}
          <div className="lg:pl-72">
            {/* Sticky top block: act-as banner (when active) above the top bar, both in flow */}
            <div className="sticky top-0 z-20">
            <ActAsBanner />
            {/* Top bar */}
            <header className="flex h-14 items-center border-b border-skin bg-surface-secondary/95 px-4 backdrop-blur-sm sm:px-6">
              {/* Mobile hamburger */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="mr-3 rounded-lg p-1.5 text-skin-secondary hover:bg-hover lg:hidden"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>

              {/* Mobile center brand */}
              <span className="lg:hidden">
                {user?.group_logo ? (
                  <img src={user.group_logo} alt={user.group_name || 'Logo'} className="max-h-8 object-contain" />
                ) : (
                  <>
                    <img src="/brand/m-bao-mark.png" alt="m-bao.fr" width={520} height={200} className="block h-8 w-auto dark:hidden" />
                    <img src="/brand/m-bao-mark-on-dark.png" alt="m-bao.fr" width={520} height={200} className="hidden h-8 w-auto dark:block" />
                  </>
                )}
              </span>

              {/* Desktop spacer */}
              <div className="hidden flex-1 lg:block" />

              {/* Mobile right: group switcher button (super_admin / impersonating / multigroup / supervisor) + messages + notification bell */}
              <div className="ml-auto flex items-center gap-2 lg:hidden">
                {isSuperAdminOrImpersonating && (
                  <button
                    onClick={handleMobileSwitcherOpen}
                    title="Administrer un groupe"
                    className="relative rounded-lg p-1.5 text-skin-secondary hover:bg-hover"
                  >
                    <UserGroupIcon className="h-5 w-5" />
                    {impersonating && (
                      <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-amber-500 ring-1 ring-surface-secondary" />
                    )}
                  </button>
                )}
                {/* Mobile: group switcher for multigroup users */}
                {showGroupSwitcher && (
                  <button
                    onClick={() => navigate('/select-group')}
                    title="Changer de groupe"
                    className="relative rounded-lg p-1.5 text-skin-secondary hover:bg-hover"
                  >
                    <UserGroupIcon className="h-5 w-5" />
                  </button>
                )}
                {/* Mobile: group switcher for supervisors */}
                {user?.role === 'supervisor' && (
                  <button
                    onClick={() => navigate('/select-group')}
                    title="Changer de groupe"
                    className="relative rounded-lg p-1.5 text-skin-secondary hover:bg-hover"
                  >
                    <UserGroupIcon className="h-5 w-5 text-purple-500" />
                  </button>
                )}
                {!actingAs && (
                  <Link to="/messages" className="relative rounded-lg p-1.5 text-skin-secondary hover:bg-hover">
                    <ChatBubbleOvalLeftEllipsisIcon className="h-5 w-5" />
                    {messagingUnreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                        {messagingUnreadCount > 9 ? '9+' : messagingUnreadCount}
                      </span>
                    )}
                  </Link>
                )}
                <NotificationBell />
              </div>

              {/* Desktop right: messages icon + notification bell + user popover */}
              <div className="ml-auto hidden items-center gap-3 lg:flex">
                {!actingAs && (
                  <Link to="/messages" className="relative rounded-lg p-1.5 text-skin-secondary hover:bg-hover">
                    <ChatBubbleOvalLeftEllipsisIcon className="h-6 w-6" />
                    {messagingUnreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                        {messagingUnreadCount > 9 ? '9+' : messagingUnreadCount}
                      </span>
                    )}
                  </Link>
                )}
                <NotificationBell />

                <Popover className="relative">
                  {({ open: popoverOpen, close: closePopover }) => (
                    <>
                      <PopoverButton
                        className={`flex items-center gap-2.5 rounded-lg px-2 py-1.5 -my-1 transition-colors hover:bg-hover focus:outline-none ${
                          popoverOpen ? 'bg-hover' : ''
                        }`}
                      >
                        {/* Avatar with amber ring when impersonating */}
                        <span className={`relative inline-flex shrink-0 ${impersonating ? 'rounded-full ring-2 ring-amber-500 ring-offset-2' : ''}`}
                          title={impersonating ? `Vue admin\u00a0: ${user?.group_name}` : undefined}
                        >
                          <UserAvatar user={user} />
                        </span>
                        <span className="text-sm/6 font-medium text-skin-secondary">
                          {getDisplayName(user)}
                        </span>
                        <ChevronDownIcon className={`h-4 w-4 text-skin-tertiary transition-transform duration-150 ${popoverOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                      </PopoverButton>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <PopoverPanel className="absolute right-0 z-10 mt-2 w-80 origin-top-right rounded-md bg-surface-elevated border border-skin shadow-dropdown focus:outline-none">
                          {/* Theme toggle row */}
                          <div className="flex items-center justify-between px-4 py-2.5">
                            <div className="flex items-center">
                              {isDark ? (
                                <MoonIcon className="mr-3 h-5 w-5 text-skin-tertiary" aria-hidden="true" />
                              ) : (
                                <SunIcon className="mr-3 h-5 w-5 text-skin-tertiary" aria-hidden="true" />
                              )}
                              <span className="text-sm text-skin-primary">
                                {isDark ? 'Mode sombre' : 'Mode clair'}
                              </span>
                            </div>
                            <Switch
                              checked={isDark}
                              onChange={toggleTheme}
                              className="group relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-surface-tertiary transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-hidden data-[checked]:bg-primary-500"
                            >
                              <span className="sr-only">Activer le mode sombre</span>
                              <span className="pointer-events-none relative inline-block h-4 w-4 transform rounded-full bg-white shadow-xs ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-4">
                                <span
                                  aria-hidden="true"
                                  className="absolute inset-0 flex h-full w-full items-center justify-center transition-opacity duration-200 ease-in group-data-[checked]:opacity-0 group-data-[checked]:duration-100 group-data-[checked]:ease-out"
                                >
                                  <SunIcon className="h-3 w-3 text-amber-500" />
                                </span>
                                <span
                                  aria-hidden="true"
                                  className="absolute inset-0 flex h-full w-full items-center justify-center opacity-0 transition-opacity duration-100 ease-out group-data-[checked]:opacity-100 group-data-[checked]:duration-200 group-data-[checked]:ease-in"
                                >
                                  <MoonIcon className="h-3 w-3 text-primary-600" />
                                </span>
                              </span>
                            </Switch>
                          </div>

                          <div className="border-t border-skin" />

                          {/* Profile + About */}
                          <div className="py-1">
                            <Link
                              to="/profile"
                              onClick={closePopover}
                              className="group flex items-center px-4 py-2 text-sm text-skin-secondary hover:bg-hover hover:text-skin-primary"
                            >
                              <UserCircleIcon
                                className="mr-3 h-5 w-5 text-skin-tertiary group-hover:text-skin-secondary"
                                aria-hidden="true"
                              />
                              Mon profil
                            </Link>
                            <Link
                              to="/about"
                              onClick={closePopover}
                              className="group flex items-center px-4 py-2 text-sm text-skin-secondary hover:bg-hover hover:text-skin-primary"
                            >
                              <InformationCircleIcon
                                className="mr-3 h-5 w-5 text-skin-tertiary group-hover:text-skin-secondary"
                                aria-hidden="true"
                              />
                              À propos
                            </Link>
                          </div>

                          {/* Group Switcher — only for super_admin or when impersonating */}
                          {isSuperAdminOrImpersonating && (
                            <>
                              <div className="border-t border-skin" />
                              <Disclosure>
                                {({ open: disclosureOpen }) => (
                                  <>
                                    <DisclosureButton
                                      onClick={fetchGroups}
                                      className="flex w-full items-center justify-between px-4 py-2.5 text-sm font-medium text-skin-secondary hover:bg-hover hover:text-skin-primary"
                                    >
                                      <span className="flex items-center gap-2">
                                        <UserGroupIcon className="h-5 w-5 text-amber-500" />
                                        Administrer un groupe
                                      </span>
                                      <ChevronDownIcon
                                        className={`h-4 w-4 text-skin-tertiary transition-transform duration-150 ${disclosureOpen ? 'rotate-180' : ''}`}
                                      />
                                    </DisclosureButton>
                                    <DisclosurePanel className="max-h-64 overflow-y-auto px-2 pb-2">
                                      <GroupSwitcherList
                                        groups={groups}
                                        loading={groupsLoading}
                                        error={groupsError}
                                        onRetry={retryFetch}
                                        currentGroupId={currentImpersonatedGroupId}
                                        onSelect={(group) => handleSelectGroup(group, closePopover)}
                                        onExit={async () => {
                                          closePopover();
                                          await handleExitImpersonation();
                                        }}
                                      />
                                    </DisclosurePanel>
                                  </>
                                )}
                              </Disclosure>
                            </>
                          )}

                          {/* Group Switcher — for multigroup regular users (member / group_admin with has_multiple_groups) */}
                          {showGroupSwitcher && (
                            <GroupSwitcherMenuItem
                              currentGroupId={user.group_id}
                              onSwitch={closePopover}
                              endpoint="/auth"
                            />
                          )}

                          {/* Group Switcher — for supervisors (uses supervisor endpoints) */}
                          {user?.role === 'supervisor' && (
                            <GroupSwitcherMenuItem
                              currentGroupId={user.group_id ?? user.impersonating_group_id}
                              onSwitch={closePopover}
                              endpoint="/supervisor"
                            />
                          )}

                          <div className="border-t border-skin" />

                          {/* Logout */}
                          <div className="py-1">
                            <button
                              onClick={() => {
                                closePopover();
                                signOut();
                              }}
                              className="group flex w-full items-center px-4 py-2 text-sm text-skin-secondary hover:bg-hover hover:text-skin-primary"
                            >
                              <ArrowRightOnRectangleIcon
                                className="mr-3 h-5 w-5 text-skin-tertiary group-hover:text-skin-secondary"
                                aria-hidden="true"
                              />
                              Se déconnecter
                            </button>
                          </div>
                        </PopoverPanel>
                      </Transition>
                    </>
                  )}
                </Popover>
              </div>
            </header>
            </div>

            {/* Page content */}
            <main className="px-4 py-6 sm:px-6 lg:px-8">
              <Outlet />
            </main>
          </div>

          {/* Mobile group switcher modal */}
          <MobileGroupSwitcherModal
            open={mobileSwitcherOpen}
            onClose={() => setMobileSwitcherOpen(false)}
            groups={groups}
            loading={groupsLoading}
            error={groupsError}
            onRetry={retryFetch}
            currentGroupId={currentImpersonatedGroupId}
            onSelect={handleMobileSelect}
            onExit={handleMobileExit}
          />
        </div>
  );
}

export default function AppLayout() {
  return (
    <SocketProvider>
      <NotificationProvider>
        <MessagingProvider>
          <AppLayoutInner />
        </MessagingProvider>
      </NotificationProvider>
    </SocketProvider>
  );
}
